'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';

interface ApplicationFormData {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  yearsOfExperience: string;
  portfolioUrl: string;
  linkedinUrl: string;
  coverLetter: string;
}

export default function CareersPage() {
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState<ApplicationFormData>({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    yearsOfExperience: '',
    portfolioUrl: '',
    linkedinUrl: '',
    coverLetter: '',
  });

  const handleApply = (positionTitle: string) => {
    setSelectedPosition(positionTitle);
    setShowApplicationModal(true);
    setSubmitSuccess(false);
  };

  const handleCloseModal = () => {
    setShowApplicationModal(false);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      location: '',
      yearsOfExperience: '',
      portfolioUrl: '',
      linkedinUrl: '',
      coverLetter: '',
    });
    setSubmitSuccess(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const applicationData = {
        position: selectedPosition,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        yearsOfExperience: formData.yearsOfExperience,
        portfolioUrl: formData.portfolioUrl,
        linkedinUrl: formData.linkedinUrl,
        coverLetter: formData.coverLetter,
      };

      const response = await fetch('/api/submit-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(applicationData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.details || result.error || 'Failed to submit application');
      }

      setIsSubmitting(false);
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      setIsSubmitting(false);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(
        `There was an error submitting your application: ${errorMessage}\n\nPlease try again or email us directly at careers@shashio.com`
      );
    }
  };

  const benefits = [
    { title: 'Competitive Salary', description: 'We offer competitive compensation packages that reward your skills and experience.', icon: '💰' },
    { title: 'Remote Work', description: 'Work from anywhere in the world. We embrace a remote-first culture and flexible schedules.', icon: '🌍' },
    { title: 'Health Benefits', description: 'Comprehensive health, dental, and vision insurance for you and your family.', icon: '🏥' },
    { title: 'Professional Growth', description: 'Continuous learning opportunities, training programs, and clear career advancement paths.', icon: '📈' },
    { title: 'Paid Time Off', description: 'Generous vacation policy, sick days, and paid holidays to maintain work-life balance.', icon: '🏖️' },
    { title: 'Team Culture', description: 'A collaborative, supportive environment where your ideas and contributions truly matter.', icon: '🤝' },
  ];

  const openPositions = [
    { title: 'Senior Photo Editor', department: 'Editing', location: 'Remote', type: 'Full-time', description: "We're looking for experienced photo editors with expertise in real estate photography enhancement." },
    { title: 'Quality Control Specialist', department: 'Operations', location: 'Remote', type: 'Full-time', description: 'Ensure every image meets our high-quality standards before delivery to clients.' },
    { title: 'Customer Success Manager', department: 'Customer Support', location: 'Remote', type: 'Full-time', description: 'Help our clients succeed by providing exceptional support and guidance.' },
    { title: 'Full Stack Developer', department: 'Engineering', location: 'Remote', type: 'Full-time', description: 'Build and maintain our platform using modern web technologies.' },
  ];

  const applicationSteps = [
    { step: '1', title: 'Submit Application', description: 'Send your resume and portfolio through our quick online form.' },
    { step: '2', title: 'Initial Review', description: 'Our team reviews your application within 3-5 business days.' },
    { step: '3', title: 'Interview Process', description: 'Video interviews with team members and hiring managers.' },
    { step: '4', title: 'Offer & Onboarding', description: 'Receive your offer and begin your journey with Shashio.' },
  ];

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.12] text-white placeholder-gray-500 transition-all duration-200 focus:border-purple-500 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm';

  const labelClass = 'block text-sm font-medium text-gray-200 mb-2';

  const SectionBadge = ({ children }: { children: React.ReactNode }) => (
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold mb-6">
      {children}
    </div>
  );

  return (
    <div className="bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== APPLICATION MODAL ===================== */}
      {showApplicationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-[#13151F] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-white/[0.12]">
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-start mb-4 md:mb-6">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    Apply for{' '}
                    <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                      {selectedPosition}
                    </span>
                  </h3>
                  <p className="text-sm md:text-base text-gray-400">
                    Fill out the form below to submit your application
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white text-3xl leading-none flex-shrink-0 ml-4 transition-colors"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {submitSuccess ? (
                <div className="text-center py-8 md:py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border border-green-500/30 mx-auto mb-6">
                    <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold text-white mb-3">Application Submitted!</h4>
                  <p className="text-gray-400 mb-6">
                    Thank you for applying. We&apos;ll review your application and get back to you within 3-5 business days.
                  </p>
                  <button
                    onClick={handleCloseModal}
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all duration-300 shadow-lg shadow-purple-500/30"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                  <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                    <div>
                      <label className={labelClass}>
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} required className={inputClass} placeholder="John Doe" />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Email Address <span className="text-red-400">*</span>
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className={inputClass} placeholder="john@example.com" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                    <div>
                      <label className={labelClass}>
                        Phone Number <span className="text-red-400">*</span>
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className={inputClass} placeholder="+1 (555) 123-4567" />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Location (City, Country) <span className="text-red-400">*</span>
                      </label>
                      <input type="text" name="location" value={formData.location} onChange={handleInputChange} required className={inputClass} placeholder="New York, USA" />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Years of Experience <span className="text-red-400">*</span>
                    </label>
                    <select name="yearsOfExperience" value={formData.yearsOfExperience} onChange={handleInputChange} required className={inputClass}>
                      <option className="bg-[#13151F]" value="">Select experience level</option>
                      <option className="bg-[#13151F]" value="0-1">0-1 years</option>
                      <option className="bg-[#13151F]" value="1-3">1-3 years</option>
                      <option className="bg-[#13151F]" value="3-5">3-5 years</option>
                      <option className="bg-[#13151F]" value="5-10">5-10 years</option>
                      <option className="bg-[#13151F]" value="10+">10+ years</option>
                    </select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                    <div>
                      <label className={labelClass}>Portfolio URL</label>
                      <input type="url" name="portfolioUrl" value={formData.portfolioUrl} onChange={handleInputChange} className={inputClass} placeholder="https://yourportfolio.com" />
                    </div>
                    <div>
                      <label className={labelClass}>LinkedIn Profile</label>
                      <input type="url" name="linkedinUrl" value={formData.linkedinUrl} onChange={handleInputChange} className={inputClass} placeholder="https://linkedin.com/in/yourprofile" />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Cover Letter <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="coverLetter"
                      value={formData.coverLetter}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className={`${inputClass} resize-none`}
                      placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                    />
                  </div>

                  <div className="flex gap-3 md:gap-4 pt-2">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 px-4 md:px-6 py-3 border border-white/[0.15] text-white rounded-xl font-bold hover:bg-white/[0.06] transition-all duration-200 text-sm md:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 px-4 md:px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all duration-300 shadow-lg shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17]">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-0 right-1/3 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-36 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium mb-8">
            <span className="text-sm">💼</span>
            <span>Careers at Shashio · Remote-First</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
            Build your career
            <br />
            <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
              with Shashio.
            </span>
          </h1>
          <p className="max-w-3xl mx-auto text-xl text-gray-400 leading-relaxed">
            Help real estate professionals worldwide showcase their properties with stunning,
            professional-quality images. Join a growing, innovative, remote-first team that values
            creativity and craft.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <a href="#positions">
              <button className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5">
                View Open Roles
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </a>
            <a href="/assessment/4dc4be27-2c2a-4563-83c9-63e38d995ca8">
              <button className="px-8 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-semibold rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                Take Skill Assessment
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* ===================== CULTURE / STATS ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <SectionBadge>OUR CULTURE</SectionBadge>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Work that{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                actually matters.
              </span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              At Shashio, we empower our team to do their best work. We foster a collaborative
              environment where everyone&apos;s voice is heard and valued.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              We&apos;re remote-first, so you can work from anywhere while being part of a truly global
              team. We value results over hours and trust our people to manage their own time.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              Whether you&apos;re a photo editor, developer, or customer success specialist, you&apos;ll make a
              real impact on our product and our customers&apos; success.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <div className="grid gap-5">
              {[
                { big: '50+', small: 'Team Members' },
                { big: '15+', small: 'Countries' },
                { big: '100%', small: 'Remote' },
              ].map((c, i) => (
                <div
                  key={i}
                  className="bg-[#13151F]/95 backdrop-blur-2xl rounded-2xl border-2 border-white/[0.12] p-8 flex items-center justify-between hover:border-purple-500/30 transition-all duration-300"
                >
                  <div className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    {c.big}
                  </div>
                  <div className="text-gray-400 font-medium">{c.small}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== BENEFITS ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <SectionBadge>BENEFITS & PERKS</SectionBadge>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              We take care of our team
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Comprehensive benefits and perks designed to support your health, happiness, and growth.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group bg-[#13151F]/80 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-8 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-2xl mb-5 group-hover:scale-110 transition-transform">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== OPEN POSITIONS ===================== */}
      <section id="positions" className="relative py-24 lg:py-32 px-6 lg:px-12 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <SectionBadge>OPEN POSITIONS</SectionBadge>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Find your role
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Explore our current openings and find the perfect fit for your skills and experience.
            </p>
          </div>
          <div className="space-y-5 max-w-5xl mx-auto">
            {openPositions.map((position, index) => (
              <div
                key={index}
                className="group bg-[#13151F]/80 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-8 hover:border-purple-500/30 transition-all duration-300"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-3">{position.title}</h3>
                    <p className="text-gray-400 mb-4">{position.description}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 text-purple-300 rounded-full text-sm font-semibold">
                        {position.department}
                      </span>
                      <span className="px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-full text-sm font-semibold">
                        {position.location}
                      </span>
                      <span className="px-4 py-1.5 bg-green-500/10 border border-green-500/20 text-green-300 rounded-full text-sm font-semibold">
                        {position.type}
                      </span>
                    </div>
                  </div>
                  <div>
                    <button
                      onClick={() => handleApply(position.title)}
                      className="group/btn px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all duration-300 shadow-lg shadow-purple-500/30 hover:-translate-y-0.5 whitespace-nowrap flex items-center gap-2"
                    >
                      Apply Now
                      <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== APPLICATION PROCESS ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <SectionBadge>APPLICATION PROCESS</SectionBadge>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Transparent and efficient
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Our hiring process is designed to be clear, quick, and respectful of your time.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {applicationSteps.map((item, index) => (
              <div
                key={index}
                className="relative bg-white/[0.03] border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-2xl font-bold text-white mb-5 shadow-lg shadow-purple-500/30">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-3xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6),0_10px_30px_rgba(124,58,237,0.15)] p-12 lg:p-16">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
              Don&apos;t see the{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                perfect role?
              </span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              We&apos;re always looking for talented real estate photo editors. Start with our professional skill assessment to showcase your editing quality. Qualified candidates will be contacted for the next stage of our hiring process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:careers@shashio.com">
                <button className="px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                  Email Your Resume
                </button>
              </a>
              <Link href="/contact">
                <button className="px-10 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-bold text-lg rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
